import { useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { apis } from '@/modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { useUserBadges } from '@/hooks/useUserBadges'
import { isAuthenticatedAtom } from '@/store/userAtoms'
import { useAtomValue } from 'jotai'
import { useLocalSettings } from '@/hooks/useLocalSettings'
const toastStaggerMs = 3000

export const releaseNotesQueryKeys = {
  all: () => ['releaseNotes'] as const,
  list: (fullList: boolean) => ['releaseNotes', fullList] as const,
}

async function fetchReleaseNotes(fullList: boolean) {
  const { data } = await apis.userService.releaseNotesGet(fullList)
  return data
}

export const useReleaseNotes = (triggerToasters = false, fullList = true) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const queryClient = useQueryClient()
  const { hasBadge } = useUserBadges()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isOnUpdatesPage = routerState.location.pathname.startsWith('/updates')
  const blocked = hasBadge(UserService.UserBadge.AssignmentOnly)

  const query = useQuery({
    queryKey: releaseNotesQueryKeys.list(fullList),
    queryFn: () => fetchReleaseNotes(fullList),
    enabled: !blocked && isAuthenticated,
  })

  const notes = blocked ? [] : (query.data?.notes ?? [])
  const unreadCount = blocked ? 0 : (query.data?.unreadCount ?? 0)
  const loading = blocked ? false : query.isPending
  const { getSetting, setSetting } = useLocalSettings()
  const announcedIdsRef = useRef<string[]>(getSetting('announcedReleaseNoteIds') ?? [])

  useEffect(() => {
    if (blocked || !triggerToasters || isOnUpdatesPage || !query.data?.notes) return
    const announcedIds = announcedIdsRef.current
    const unread = query.data.notes.filter((note) => note.unread)
    const toAnnounce = unread.filter((note) => !announcedIds.includes(note.id))
    if (toAnnounce.length === 0) return
    if (toAnnounce.length >= 3) {
      toast(t('releaseNotes.XPLabUpdate'), {
        description: t('releaseNotes.newUpdatesAvailable'),
        action: {
          label: t('releaseNotes.goToWhatsNew'),
          onClick: () => navigate({ to: '/updates' }),
        },
      })
    } else {
      toAnnounce.forEach((note, i) => {
        toast(t('releaseNotes.XPLabUpdate'), {
          description: note.title,
          duration: (toAnnounce.length - i) * toastStaggerMs,
          action: {
            label: t('releaseNotes.goToWhatsNew'),
            onClick: () => navigate({ to: '/updates' }),
          },
        })
      })
    }
    const updated = [...announcedIds, ...toAnnounce.map((n) => n.id)].slice(-15)
    announcedIdsRef.current = updated
    setSetting('announcedReleaseNoteIds', updated)
  }, [blocked, triggerToasters, query.data?.notes, t, navigate, setSetting])

  const fetchNotes = useCallback(
    (_fullList?: boolean) => {
      if (blocked) return Promise.resolve()
      return queryClient.refetchQueries({ queryKey: releaseNotesQueryKeys.all() })
    },
    [queryClient, blocked]
  )

  const markAsRead = useCallback(async () => {
    if (blocked) return
    await apis.userService.releaseNotesReadPost()
    await queryClient.refetchQueries({ queryKey: releaseNotesQueryKeys.all() })
  }, [queryClient, blocked])

  return {
    notes,
    unreadCount,
    loading,
    fetchNotes,
    markAsRead,
    /** True when release notes are hidden for this user (e.g. assignmentOnly badge). */
    isBlocked: blocked,
  }
}
